/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { AudienceType, MessageType } from '../../../algorithm/types';
import { analyticsTrack } from '../../../analytics_utils';
import { removeFiltersFromAudience } from '../../../utils';
import { MultipleMatches } from './InnerMessageExtra/MultipleMatches';
import { confirmMessageNext } from '../../chatSlice';
import { selectSearchSourceType, selectPearlUserInfo } from '../../../Login/userSlice';
import styles from './InnerMessageExtra.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useAudienceFilter } from '../../hooks/ChatContext';
import { WelcomeMessage } from './InnerMessageExtra/WelcomeMessage';
import { useMessageIndex } from '../../hooks/MessageContext';
import { slugToUrl } from './utils';
import { SelectKeysSummaryTable } from './SelectKeysSummaryTable';
import { external_link_to_href } from '../../../algorithm/runAlgorithm';

type InnerMessageExtraProps = NonNullable<MessageType['extra']>;

function InnerMessageNoSearchMatch(args: InnerMessageExtraProps & { type: 'NO_SEARCH_MATCH' }): JSX.Element {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const audienceFilter = useAudienceFilter();
  const searchSourceType = useAppSelector(selectSearchSourceType);
  const { searchTerm, searchId, howActivated } = args;
  useEffect(() => {
    /*  Before making changes to this, inform:
        Cynthia.Walsh@providence.org
        Prateek.Fulsundar@providence.org

        If you modify this, please update the documentation at:
        https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/158662658/Mixpanel+and+Pendo

        The search_no_result event is sent when the user types something, and no trigger matches, and no search results match.
        Semantic search (AI) matches don't count.

        term: The original search term the user typed
        persona: The persona filter the user had selected - e.g. "Provider"
        careCategory: The care category filter the user had selected - e.g. "Outpatient"
        patientGroup: The patient group filter the user had selected - e.g. "Adult and Peds"
        howActivated: How the user activated the search - one of 'userTyped', 'algorithmClick', 'urlLink', 'teamsBot'
        numResults: The number of results found, capped to 20 on first search
        searchId: A random ID just for this search, so we can match up events like search and search_multiple_results_then_click
        aiSuggestions: The AI suggestions that were shown, as:
                       { title, score, docType }[]
        fuzzySuggestions: The fuzzy suggestions that were shown, if any, as string[]
        session_id: The session ID for this user, if logged in
     */
    analyticsTrack(
      'search_no_result',
      {
        term: searchTerm,
        persona: audienceFilter?.persona || 'Provider',
        careCategory: audienceFilter?.careCategory || 'Outpatient',
        patientGroup: audienceFilter?.patientGroup || 'Adult and Peds',
        howActivated,
        numResults: 0,
        searchId: searchId,
        session_id: pearlUserInfo?.sessionId,
        search_source: searchSourceType,
      },
      pearlUserInfo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className={styles.container}>
        <div>
          Search for <b> {searchTerm} </b>
        </div>
      </div>
    </div>
  );
}

function InnerMessageExtraWrongAudience(
  args: InnerMessageExtraProps & { type: 'WRONG_AUDIENCE' },
  audience: AudienceType
): JSX.Element {
  const { document_audiences, title, slug, document_version_id, searchTerm, publishedOnly } = args;
  const linkAudience = removeFiltersFromAudience(document_audiences, audience);
  return (
    <Link
      to={slugToUrl(slug, title, searchTerm, document_version_id, publishedOnly, linkAudience)}
      className={styles.link}
    >
      <i>Open this document:</i>
      <ul>
        <li>
          <div className={styles.audience}>
            <div className={styles.title}>{title}</div>
          </div>
        </li>
      </ul>
    </Link>
  );
}

/** This is used for special messages - like search match not found
 */
export function InnerMessageExtra(args: InnerMessageExtraProps): JSX.Element | null {
  const audienceFilter = useAudienceFilter();
  const dispatch = useAppDispatch();
  const messageIndex = useMessageIndex();

  function handleNext() {
    dispatch(confirmMessageNext({ messageIndex }));
  }

  if (args.type === 'NO_SEARCH_MATCH') {
    return InnerMessageNoSearchMatch(args);
  } else if (args.type === 'MULTIPLE_MATCHES') {
    return MultipleMatches(args);
  } else if (args.type === 'WELCOME_MESSAGE') {
    return <WelcomeMessage />;
  } else if (args.type === 'START_PAGE') {
    return (
      <span>
        {args.document_name}
        {args.page_name ? (
          <small>
            <code> #{args.page_name}</code>
          </small>
        ) : null}
      </span>
    );
  } else if (args.type === 'OPEN_LINK') {
    let text = args.external_link?.link;
    if (text?.startsWith('#')) text = text.slice(1);
    return (
      <span>
        <b>Open:</b> <Link to={external_link_to_href(args.external_link)}>{text}</Link>
      </span>
    );
  } else if (args.type === 'MATCH_FOUND') {
    return (
      <div className={styles.matchFound}>
        {/* Shows 'One match found' or 'One exact match found' also we need the labels for e2e tests */}
        One {args.was_partial_match ? '' : 'exact'} match found
        {/* Just for e2e testing to get the document version id */}
        <span data-testid="match-found" data-document-version-id={args.document_version_id} />
      </div>
    );
  } else if (args.type === 'WRONG_AUDIENCE') {
    return InnerMessageExtraWrongAudience(args, audienceFilter);
  } else if (args.type === 'NO_PAGES') {
    return <i>This document has no guides or algorithm</i>;
  } else if (args.type === 'ALGORITHM_PAGE') {
    return (
      <>
        {args.algorithm_error ? (
          <div className={styles.error}>
            <b>Error:</b> {args.algorithm_error}
          </div>
        ) : null}
        {args.need_confirmation || args.cant_confirm_error ? (
          <div className={styles.nextButton}>
            <Button
              variant="contained"
              color={args.cant_confirm_error ? 'error' : 'primary'}
              disabled={args.have_confirmation || !!args.cant_confirm_error}
              endIcon={<ArrowForwardIosIcon />}
              disableRipple
              onClick={args.cant_confirm_error ? undefined : handleNext}
            >
              {args.cant_confirm_error || 'Next'}
            </Button>
          </div>
        ) : args.has_only_leaf_pages ? (
          <div data-id="summary-output">
            <SelectKeysSummaryTable messageIndex={messageIndex - 1} />
          </div>
        ) : null}
      </>
    );
  } else {
    return null;
  }
}
